import { useTraduccion } from '@tallerya/i18n';
import { Boton, Chip, IconoMas, useAvisos } from '@tallerya/ui';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ActivoExistente } from '../activos/datos';
import { VentanaActivo } from '../activos/FormularioActivo';
import { buscarPorPlaca, leerActivo, useBuscarPorPlaca, type ActivoEncontrado } from './datos';

// Elegir el carro de una orden por su placa (#131), al abrirla y al editar su
// cabecera. Se escribe la placa y:
//
//   - si el carro existe, aparece con su cliente y se elige con un toque;
//   - si ninguna placa coincide **exacta**, «Registrar P-123ABC» abre el formulario
//     del activo en una ventana, con la placa ya puesta, y al guardar queda
//     elegido. Exacta y no «sin resultados»: la búsqueda es por contenido, y la
//     placa nueva `P123` no puede quedar tapada por un `P123ABC` que ya existe.
//
// Elegido, se muestra como una tarjeta con «Cambiar».

/** Lo que la orden necesita del carro elegido, venga de la búsqueda o de la ventana. */
export interface VehiculoElegido {
  id: string;
  identificador: string;
  /** «Toyota Hilux 2019», o el tipo si no tiene marca. */
  descripcion: string;
  clienteId: string | null;
  clienteNombre: string | null;
  /**
   * El dueño está inactivo (#177): no se le abren órdenes. Solo lo sabe la
   * búsqueda —y «Usar ese activo», que la usa (#147)—; el carro que trae la orden
   * o el recién registrado no lo dicen.
   */
  clienteInactivo?: boolean;
}

/** «Toyota Hilux 2019», sin huecos por lo que falte. */
export function descripcionDe(a: {
  brandName?: string | null;
  modelName?: string | null;
  year?: number | string | null;
}): string {
  return [a.brandName, a.modelName, a.year].filter((x) => x !== null && x !== undefined && x !== '').join(' ');
}

/** La placa como la compara la API: solo letras y dígitos, en mayúsculas. `p 123-abc` es `P123ABC`. */
const claveDePlaca = (texto: string) => texto.toUpperCase().replace(/[^A-Z0-9]/g, '');

function deLaBusqueda(a: ActivoEncontrado): VehiculoElegido {
  return {
    id: String(a.id),
    identificador: a.identifier ?? a.code ?? '',
    descripcion: descripcionDe(a) || a.assetTypeName,
    clienteId: a.customerId ? String(a.customerId) : null,
    clienteNombre: a.customerName,
    clienteInactivo: a.customerIsActive === false,
  };
}

export function ElegirVehiculo({
  elegido,
  onElegir,
  onQuitar,
  error,
  id: idExterno,
}: {
  elegido: VehiculoElegido | null;
  onElegir: (vehiculo: VehiculoElegido) => void;
  onQuitar: () => void;
  /** El error ya traducido, para mostrarlo bajo la placa. */
  error?: string | undefined;
  /** El id del campo en el formulario, para que el foco llegue aquí cuando falla. */
  id?: string;
}) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const idPropio = useId();
  const id = idExterno ?? idPropio;
  const [placa, setPlaca] = useState('');
  const [registrando, setRegistrando] = useState<string | null>(null);

  // Si la persona se va de aquí mientras se lee el carro recién registrado
  // —«Abrir sin vehículo»—, lo que llegue tarde ya no se elige.
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  // La placa se busca cuando se deja de escribir, no en cada letra.
  const [buscada, setBuscada] = useState('');
  useEffect(() => {
    const espera = setTimeout(() => setBuscada(placa), 300);
    return () => clearTimeout(espera);
  }, [placa]);
  const encontrados = useBuscarPorPlaca(elegido ? '' : buscada);

  /** El carro que se acaba de registrar en la ventana: se lee su ficha y se elige. */
  async function elegirRegistrado(idActivo: string) {
    try {
      const ficha = await leerActivo(idActivo);
      if (!montado.current) return;
      if (!ficha) throw new Error('El activo recién creado no se encontró');
      onElegir({
        id: String(ficha.id),
        identificador: ficha.identifier ?? ficha.code ?? '',
        descripcion: descripcionDe(ficha) || ficha.assetTypeName,
        clienteId: ficha.customerId ? String(ficha.customerId) : null,
        clienteNombre: ficha.customerName ?? null,
      });
    } catch {
      // El carro sí quedó registrado: buscándolo por su placa aparece.
      if (montado.current) avisar({ titulo: t('admin.ordenes.registradoSinElegir') });
    }
  }

  /**
   * «Usar ese activo →» (#147): la placa ya la tenía otro activo. Se elige por la
   * búsqueda, que trae si su dueño está inactivo, igual que si se lo hubiera
   * buscado. Si no aparece, la placa queda escrita y se elige de la lista, con sus marcas.
   */
  async function elegirExistente(existente: ActivoExistente) {
    try {
      const encontrados = await buscarPorPlaca(existente.identifier, 20);
      if (!montado.current) return;
      const suyo = encontrados.find((a) => String(a.id) === String(existente.id));
      if (suyo) {
        onElegir(deLaBusqueda(suyo));
        return;
      }
    } catch {
      // Sin respuesta: se deja buscado, como abajo.
    }
    if (montado.current) setPlaca(existente.identifier);
  }

  if (elegido) {
    return (
      <div className="orden-vehiculo__elegido">
        <div>
          <strong>{elegido.identificador}</strong>
          <span>{[elegido.descripcion, elegido.clienteNombre].filter(Boolean).join(' · ')}</span>
          {elegido.clienteInactivo && <Chip>{t('admin.ordenes.clienteInactivo')}</Chip>}
        </div>
        <Boton
          variante="texto"
          onClick={() => {
            setPlaca('');
            setBuscada('');
            onQuitar();
          }}
        >
          {t('admin.ordenes.cambiarVehiculo')}
        </Boton>
      </div>
    );
  }

  // Lo que se muestra es lo que se buscó: mientras la placa escrita no termina de
  // asentarse, no se ofrece registrar una placa vieja.
  const asentada = placa === buscada;
  const lista = asentada && buscada.trim().length >= 2 ? encontrados.data : undefined;
  const exacta = lista?.some((a) => claveDePlaca(a.identifier ?? '') === claveDePlaca(buscada)) ?? false;
  const puedeRegistrar = lista !== undefined && !exacta && claveDePlaca(buscada).length > 0;

  return (
    <>
      <div className={`ui-campo ui-campo--largo${error ? ' ui-campo--error' : ''}`}>
        <div className="ui-campo__cabecera">
          <label htmlFor={id} className="ui-campo__etiqueta">
            {t('admin.ordenes.placa')}
          </label>
        </div>
        <div className="ui-campo__caja">
          <input
            id={id}
            type="text"
            value={placa}
            placeholder={t('admin.ordenes.placa.ejemplo')}
            autoComplete="off"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={(e) => setPlaca(e.target.value)}
            // Enter busca, no abre la orden: el campo vive dentro de su formulario.
            // Con una sola coincidencia, la elige.
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const unica = lista?.length === 1 ? lista[0] : undefined;
              if (unica) onElegir(deLaBusqueda(unica));
            }}
          />
        </div>
        {error && (
          <p id={`${id}-error`} className="ui-campo__ayuda">
            {error}
          </p>
        )}
      </div>

      {encontrados.isError && <p className="orden-vehiculo__nota">{t('admin.ordenes.errorBusqueda')}</p>}

      {lista && lista.length > 0 && (
        <ul className="orden-vehiculo__encontrados" aria-label={t('admin.ordenes.encontrados')}>
          {lista.map((a) => (
            <li key={String(a.id)}>
              <button type="button" onClick={() => onElegir(deLaBusqueda(a))}>
                <strong>{a.identifier ?? a.code}</strong>
                <span>{[descripcionDe(a) || a.assetTypeName, a.customerName].filter(Boolean).join(' · ')}</span>
                {!a.isActive && <Chip>{t('admin.ordenes.desactivado')}</Chip>}
                {a.customerIsActive === false && <Chip>{t('admin.ordenes.clienteInactivo')}</Chip>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {lista && puedeRegistrar && (
        <div className="orden-vehiculo__sin-coincidencias">
          {lista.length === 0 && <p>{t('admin.ordenes.noExistePlaca')}</p>}
          <Boton variante="secundario" icono={<IconoMas />} onClick={() => setRegistrando(buscada.trim())}>
            {t('admin.ordenes.registrar', { placa: buscada.trim() })}
          </Boton>
        </div>
      )}

      {/* Fuera del formulario de la orden: la ventana es un `<dialog>` en línea, y
          adentro del `<form>` un Enter en sus campos enviaría la orden. */}
      {createPortal(
        <VentanaActivo
          identificador={registrando}
          onCancelar={() => setRegistrando(null)}
          onCreado={(idActivo) => {
            setRegistrando(null);
            void elegirRegistrado(idActivo);
          }}
          onExistente={(existente) => {
            setRegistrando(null);
            void elegirExistente(existente);
          }}
        />,
        document.body,
      )}
    </>
  );
}
